import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DateRangeSelector } from '../components/DateRangeSelector.js';
import { AuthStatusBar } from '../components/errors/AuthStatusBar.js';
import { ErrorMessageRow } from '../components/errors/ErrorMessageRow.js';
import { ScreenErrorRow } from '../components/errors/ScreenErrorRow.js';
import { SortHeader, type SortDir, type SortKey } from '../components/errors/SortHeader.js';
import { SummaryCard } from '../components/errors/SummaryCard.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { errorsApi } from '../lib/api.js';
import { formatDate, formatNumber } from '../lib/formatters.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business エラー分析ページ。日次エラー率トレンド・エラー種別内訳・画面別エラー・ログイン状態別・TOP30メッセージを可視化
 * @graph-connects errorsApi [calls] Errors APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDate [calls] 日付フォーマット
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 * @graph-connects SummaryCard [calls] サマリーカード描画
 * @graph-connects AuthStatusBar [calls] 認証ステータス別エラーバー描画
 * @graph-connects SortHeader [calls] ソートヘッダー描画
 * @graph-connects ScreenErrorRow [calls] 画面別エラー行描画
 * @graph-connects ErrorMessageRow [calls] エラーメッセージ行描画
 */
export const Route = createFileRoute('/errors')({
  component: ErrorsPage,
});

/** @graph-connects none */
const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
];

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function ErrorsPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');
  const [sortKey, setSortKey] = useState<SortKey>('count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const ratesQuery = useQuery({
    queryKey: queryKeys.errors.dailyRates(startDate, endDate),
    queryFn: () => errorsApi.getDailyRates(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const breakdownQuery = useQuery({
    queryKey: queryKeys.errors.breakdown(startDate, endDate),
    queryFn: () => errorsApi.getBreakdown(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const messagesQuery = useQuery({
    queryKey: queryKeys.errors.messages(startDate, endDate),
    queryFn: () => errorsApi.getMessages(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedScreenErrors = breakdownQuery.data
    ? [...breakdownQuery.data.byScreen].sort((a, b) => {
        const mul = sortDir === 'desc' ? -1 : 1;
        if (sortKey === 'screen') return mul * a.screen.localeCompare(b.screen);
        if (sortKey === 'event') return mul * a.event.localeCompare(b.event);
        return mul * (a[sortKey] - b[sortKey]);
      })
    : [];

  const isLoading = ratesQuery.isLoading || breakdownQuery.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('errors.title')}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">{t('errors.description')}</p>
        </div>
        <DateRangeSelector
          preset={preset}
          startDate={startDate}
          endDate={endDate}
          onPresetChange={setPreset}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card">
              <div className="skeleton-shimmer h-20 w-full rounded-lg" />
            </div>
          ))}
        </div>
      ) : ratesQuery.data ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryCard
            label={t('errors.totalErrors')}
            value={formatNumber(ratesQuery.data.summary.totalErrors)}
            color="var(--color-error)"
          />
          <SummaryCard
            label={t('errors.avgErrorRate')}
            value={`${(ratesQuery.data.summary.avgErrorRate * 100).toFixed(2)}%`}
            color="var(--color-warning)"
          />
          <SummaryCard
            label={t('errors.sessionsAffected')}
            value={formatNumber(ratesQuery.data.summary.sessionsAffected)}
            color="var(--color-accent-primary)"
          />
        </div>
      ) : null}

      {!isLoading && ratesQuery.data && ratesQuery.data.byAuthStatus.length > 0 ? (
        <div className="card animate-fade-in-up">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">
            {t('errors.byAuthStatus')}
          </h2>
          <AuthStatusBar data={ratesQuery.data.byAuthStatus} t={t} />
        </div>
      ) : null}

      {isLoading ? (
        <div className="card">
          <div className="skeleton-shimmer h-64 w-full rounded-lg" />
        </div>
      ) : ratesQuery.data ? (
        <div className="card animate-fade-in-up">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
            {t('errors.dailyTrend')}
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={ratesQuery.data.dailyRates}
                margin={{ top: 4, right: 12, bottom: 0, left: 12 }}
              >
                <defs>
                  <linearGradient id="errorRateGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-error)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-error)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v: number) => `${(v * 100).toFixed(1)}%`}
                  tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={50}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '11px',
                  }}
                  formatter={
                    ((value: number) => [
                      `${(value * 100).toFixed(2)}%`,
                      t('errors.errorRate'),
                    ]) as never
                  }
                  labelFormatter={formatDate as never}
                />
                <Area
                  type="monotone"
                  dataKey="errorRate"
                  stroke="var(--color-error)"
                  fill="url(#errorRateGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {isLoading ? (
          <div className="card">
            <div className="skeleton-shimmer h-64 w-full rounded-lg" />
          </div>
        ) : breakdownQuery.data && breakdownQuery.data.byType.length > 0 ? (
          <div className="card animate-fade-in-up">
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
              {t('errors.byType')}
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={breakdownQuery.data.byType.map((item, idx) => ({
                      ...item,
                      fill: CHART_COLORS[idx % CHART_COLORS.length],
                      fillOpacity: 0.8,
                    }))}
                    dataKey="count"
                    nameKey="event"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={
                      (({ event, percent }: { event: string; percent: number }) =>
                        `${event} (${(percent * 100).toFixed(0)}%)`) as never
                    }
                    labelLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--color-bg-elevated)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                    formatter={
                      ((value: number) => [formatNumber(value), t('errors.count')]) as never
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="card animate-fade-in-up flex items-center justify-center h-64">
            <p className="text-sm text-[var(--color-text-muted)]">{t('common.noData')}</p>
          </div>
        )}

        {isLoading ? (
          <div className="card">
            <div className="skeleton-shimmer h-64 w-full rounded-lg" />
          </div>
        ) : sortedScreenErrors.length > 0 ? (
          <div className="card animate-fade-in-up">
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
              {t('errors.byScreen')}
            </h2>
            <div className="overflow-auto max-h-64">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <SortHeader
                      label={t('errors.screen')}
                      sortKey="screen"
                      currentKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={t('errors.event')}
                      sortKey="event"
                      currentKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={t('errors.count')}
                      sortKey="count"
                      currentKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    <SortHeader
                      label={t('errors.uniqueSessions')}
                      sortKey="uniqueSessions"
                      currentKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    <th className="py-2 px-2 text-[var(--color-text-muted)] font-medium text-center">
                      {t('errors.loggedIn')} / {t('errors.anonymous')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedScreenErrors.map((row) => (
                    <ScreenErrorRow key={`${row.screen}-${row.event}`} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="card animate-fade-in-up flex items-center justify-center h-64">
            <p className="text-sm text-[var(--color-text-muted)]">{t('common.noData')}</p>
          </div>
        )}
      </div>

      {messagesQuery.isLoading ? (
        <div className="card">
          <div className="skeleton-shimmer h-64 w-full rounded-lg" />
        </div>
      ) : messagesQuery.data && messagesQuery.data.messages.length > 0 ? (
        <div className="card animate-fade-in-up">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
            {t('errors.topMessages')}
          </h2>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="py-2 px-2 text-left text-[var(--color-text-muted)] font-medium">
                    {t('errors.message')}
                  </th>
                  <th className="py-2 px-2 text-left text-[var(--color-text-muted)] font-medium">
                    {t('errors.screen')}
                  </th>
                  <th className="py-2 px-2 text-left text-[var(--color-text-muted)] font-medium">
                    {t('errors.event')}
                  </th>
                  <th className="py-2 px-2 text-right text-[var(--color-text-muted)] font-medium">
                    {t('errors.count')}
                  </th>
                  <th className="py-2 px-2 text-right text-[var(--color-text-muted)] font-medium">
                    {t('errors.uniqueSessions')}
                  </th>
                  <th className="py-2 px-2 text-center text-[var(--color-text-muted)] font-medium">
                    {t('errors.loggedIn')} / {t('errors.anonymous')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {messagesQuery.data.messages.map((msg, idx) => (
                  <ErrorMessageRow key={idx} msg={msg} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card animate-fade-in-up">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
            {t('errors.topMessages')}
          </h2>
          <p className="text-sm text-[var(--color-text-muted)]">{t('common.noData')}</p>
        </div>
      )}
    </div>
  );
}
